import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import RootState from '@vue-storefront/core/types/RootState'
import TagsState from '../../types/TagsState'
import Tag from '../../types/Tag'
import { processURLAddress } from '@vue-storefront/core/helpers'
import fetch from 'isomorphic-fetch'
import config from 'config'
import { router } from '@vue-storefront/core/app'

export const tagsScope = ({ categoryId = null, locationCode = null }) => `${categoryId || ''}|${locationCode || ''}`

// Shares one fetch between overlapping callers for the same scope. Module state
// is per SSR request (the bundle renderer runs each request in a new context).
let tagsInFlight: { key: string, promise: Promise<any> } | null = null

const actions: ActionTree<TagsState, RootState> = {
  // Loads tags unless they're already loaded (or loading) for this category + city.
  ensureTags ({ state, dispatch }, payload) {
    const key = tagsScope(payload)
    if (state.loadedFor === key) {
      return Promise.resolve()
    }
    if (!tagsInFlight || tagsInFlight.key !== key) {
      const promise = dispatch('loadTags', payload).finally(() => {
        if (tagsInFlight && tagsInFlight.promise === promise) {
          tagsInFlight = null
        }
      })
      tagsInFlight = { key, promise }
    }
    return tagsInFlight.promise
  },
  async loadTags ({commit}, payload) {
    let url = processURLAddress(`${config.partners.base_endpoint}/list/tags`)
    let param = payload.categoryId ? `?categoryId=${payload.categoryId}&locationCode=${payload.locationCode}`: `?locationCode=${payload.locationCode}`
    await fetch(url + `${param}`, {
      method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(response => {
        commit(types.SET_PARTNER_TAGS, response.result)
        commit(types.SET_TAGS_LOADED_FOR, tagsScope(payload))
        return response
      })
      .catch(error => {
        commit(types.SET_TAGS_LOADED_FOR, null)
        throw new Error('FetchError in request to ES: ' + error.toString())
      })
  },
  async toggleTag ({ dispatch, commit, state }, payload: Tag) {
    commit(types.TOGGLE_PARTNER_TAG, payload)
    let activeTags = state.selected_tags
    if (activeTags.length > 0) {
      return await dispatch('changeAppliedTags', activeTags[0].code)
    } else {
      return await dispatch('resetAppliedTags')
    }
  },
  async resetAppliedTags ({ dispatch, commit }) {
    commit(types.CLEAN_SELECTED_TAGS)
    await dispatch('changeAppliedTags', false)
  },
  // Same state reset as resetAppliedTags but without its router.push - for
  // syncing state to a navigation that already landed on a tag-less URL
  // (e.g. SubCategory.vue's asyncData, which reruns on every client nav),
  // where pushing again would re-trigger navigation mid-resolve.
  resetSelectedTags ({ commit }) {
    commit(types.CLEAN_SELECTED_TAGS)
  },
  selectTag ({ commit }, tag: Tag) {
    commit(types.CLEAN_SELECTED_TAGS)
    commit(types.TOGGLE_PARTNER_TAG, tag)
  },
  // K2-2985: pushes the clean /{city}/{category}/{tag} path instead of the old
  // ?tags= query string, so tag pages are indexable, distinct URLs.
  async changeAppliedTags ({ rootGetters }, tagsList) {
    const location = rootGetters['locations/getActiveLocation']
    const category = rootGetters['partner_categories/getActivePartnersCategory']
    const { tags, ...query } = router.currentRoute.query
    if (location && category && category.code) {
      const path = `/${location.code}/${category.code}` + (tagsList ? `/${tagsList}` : '')
      router.push({ path, query })
    } else {
      router.push({ query: tagsList ? { ...query, tags: tagsList } : query })
    }
  },
}

export default actions
